import axios from "axios";
import { X } from "lucide-react";
import { useEffect, useState } from "react";
import {
  FiChevronLeft,
  FiChevronRight,
  FiClock,
  FiLock,
  FiRefreshCw,
  FiUnlock,
} from "react-icons/fi";
import { ImSpinner2 } from "react-icons/im";
import { MdBlock, MdCheckCircle } from "react-icons/md";
import Breadcrumb from "../../../components/AdminSideComponents/BreadCrumb";
import {
  convertTo12Hour,
  formatMonthYear,
} from "../../../utils/formatDateTime";
import {
  confirmAction,
  showError,
  showSuccess,
} from "../../../utils/toastUtils";
import { useAdminAuth } from "../../../context/AdminAuthContext";

/* ---------------- helpers ---------------- */

const getTodayISO = () => new Date().toISOString().split("T")[0];

const addDays = (dateStr, days) => {
  const date = new Date(dateStr);
  date.setDate(date.getDate() + days);
  return date.toISOString().split("T")[0];
};

/* ---------------- status badge ---------------- */

const SlotStatusBadge = ({ isBlocked, slotStatus }) => {
  if (isBlocked) {
    return (
      <div className="flex items-center justify-center gap-0.5 py-0.5 w-20 rounded-full bg-red-100 border border-red-300">
        <MdBlock className="text-red-600 text-sm" />
        <span className="text-[0.65rem] font-semibold text-red-700">
          Blocked
        </span>
      </div>
    );
  }

  if (slotStatus === "inactive") {
    return (
      <div className="flex items-center justify-center gap-0.5 py-0.5 w-20 rounded-full bg-gray-200 border border-gray-300">
        <span className="text-[0.65rem] font-semibold text-gray-600">
          Inactive
        </span>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center gap-0.5 py-0.5 w-20 rounded-full bg-green-100 border border-green-300">
      <MdCheckCircle className="text-green-600 text-xs" />
      <span className="text-[0.65rem] font-semibold text-green-700">
        Available
      </span>
    </div>
  );
};

/* ================= MAIN ================= */

const AdminSlotsAvailability = () => {
  const { admin, isAdmin, isManager } = useAdminAuth();

  const DAYS_WINDOW = 7;
  const AUTO_GENERATE_DAYS = 7;

  const todayISO = getTodayISO();

  const [branches, setBranches] = useState([]);
  const [branchesLoading, setBranchesLoading] = useState(false);
  const [selectedBranchId, setSelectedBranchId] = useState(null);

  const [selectedDate, setSelectedDate] = useState(todayISO);
  const [windowStartDate, setWindowStartDate] = useState(todayISO);
  const canNavigatePrev = windowStartDate > todayISO;

  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);

  const [showBlockModal, setShowBlockModal] = useState(null);
  const [showGenerateModal, setShowGenerateModal] = useState(false);

  useEffect(() => {
    if (isManager && admin?.assignedBranch) {
      setSelectedBranchId(admin.assignedBranch?._id);
      return;
    }

    if (isAdmin) {
      fetchBranches();
    }
  }, [admin, isAdmin, isManager]);

  const fetchBranches = async () => {
    setBranchesLoading(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/branches/get-all`,
      );
      const data = res.data?.data || [];
      setBranches(data);
      if (data.length) setSelectedBranchId(data[0]._id);
    } catch {
      showError("Failed to fetch branches");
    } finally {
      setBranchesLoading(false);
    }
  };

  const fetchAvailability = async (date, branchId) => {
    if (!branchId) return;

    setLoading(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/slot-availability/by-date`,
        {
          params: { date, branchId },
        },
      );
      setSlots(res.data?.data || []);
    } catch {
      showError("Failed to fetch availability");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedBranchId) {
      fetchAvailability(selectedDate, selectedBranchId);
    }
  }, [selectedDate, selectedBranchId]);

  const isBeyondAutoWindow = (date) => {
    const today = new Date(todayISO);
    const target = new Date(date);
    const diff = (target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24);
    return diff >= AUTO_GENERATE_DAYS;
  };

  const getVisibleDates = () =>
    Array.from({ length: DAYS_WINDOW }, (_, i) => addDays(windowStartDate, i));

  const navigateDateWindow = (dir) => {
    if (dir < 0 && !canNavigatePrev) return;
    setWindowStartDate((p) => addDays(p, dir * DAYS_WINDOW));
  };

  const goToToday = () => {
    setWindowStartDate(todayISO);
    setSelectedDate(todayISO);
  };

  const toggleBlockStatus = async (slot) => {
    if (slot.isBlocked) {
      const confirmed = await confirmAction({
        title: "Unblock this slot?",
        text: "Users will be able to book this slot again.",
      });
      if (!confirmed) return;

      await axios.patch(
        `${process.env.REACT_APP_API_URL}/slot-availability/block/${slot.availabilityId}`,
        { isBlocked: false, branchId: selectedBranchId },
      );

      setSlots((prev) =>
        prev.map((s) =>
          s.availabilityId === slot.availabilityId
            ? { ...s, isBlocked: false, }
            : s,
        ),
      );
      showSuccess("Slot unblocked successfully");
    } else {
      setShowBlockModal(slot);
    }
  };

  const handleBlockSlot = async (slot) => {
    await axios.patch(
      `${process.env.REACT_APP_API_URL}/slot-availability/block/${slot.availabilityId}`,
      { isBlocked: true, branchId: selectedBranchId },
    );

    setSlots((prev) =>
      prev.map((s) =>
        s.availabilityId === slot.availabilityId
          ? { ...s, isBlocked: true, }
          : s,
      ),
    );
    showSuccess("Slot blocked successfully");
    setShowBlockModal(null);
  };

  const generateBtn = () => {
    return (
      <button
        onClick={() => setShowGenerateModal(true)}
        className="flex items-center gap-2 px-4 py-2.5 text-sm rounded-lg bg-gradient-to-r from-[#f0653a] to-[#d94f2a] text-white font-medium  hover:scale-[1.02] active:scale-[0.98] transition-all duration-200"
      >
        <FiRefreshCw className="text-lg" />
        Generate Slots
      </button>
    );
  };
  return (
    <div className="bg-gradient-to-br px-6 min-h-screen from-[#FFF7F3] to-gray-50 py-2">
      <div className="max-w-full mx-auto">
        <Breadcrumb
          title1="Slots"
          title2="Availability Calendar"
          customButton={generateBtn}
        />

        {isAdmin && (
          <div className="bg-white rounded-lg shadow-md border mb-4 p-2">
            {branchesLoading ? (
              <div className="flex gap-2 animate-pulse">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="h-8 w-32 bg-gray-200 rounded-lg" />
                ))}
              </div>
            ) : (
              <div className="flex gap-2 overflow-x-auto no-scrollbar">
                {branches.map((b) => {
                  const active = selectedBranchId === b._id;
                  return (
                    <button
                      key={b._id}
                      onClick={() => setSelectedBranchId(b._id)}
                      className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${active
                        ? "bg-gradient-to-r from-accentTextColor to-accentTextColor/80 text-white shadow"
                        : "bg-gray-100 text-gray-600 hover:bg-brandAccentColor/40"
                        }`}
                    >
                      {b.branchName}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Date Navigator */}
        <div className="bg-white rounded-lg shadow-xl p-4 my-4 border border-gray-100 overflow-x-auto ">
          <div className="flex flex-col">
            <div className="flex justify-center">
              <span className="text-sm font-semibold text-secondaryTextColor">
                {formatMonthYear(windowStartDate)}
              </span>
            </div>

            {/* Top Row: arrows + date cells */}
            <div className="flex items-center justify-between gap-4">
              <button
                onClick={() => navigateDateWindow(-1)}
                disabled={!canNavigatePrev}
                className={`p-3 rounded-xl transition-colors ${canNavigatePrev
                  ? "hover:bg-gray-100 text-gray-600"
                  : "text-gray-300 cursor-not-allowed"
                  }`}
              >
                <FiChevronLeft className="text-xl" />
              </button>

              <div className="flex-1 flex items-center justify-center gap-3 py-4">
                {getVisibleDates()?.map((date) => {
                  const isActive = date === selectedDate;
                  return (
                    <button
                      key={date}
                      onClick={() => setSelectedDate(date)}
                      className={`px-4 py-3 w-20 rounded-xl border text-sm font-semibold transition-all ${isActive
                        ? "bg-gradient-to-r from-accentTextColor/70 to-accentTextColor text-white border-accentTextColor/50 scale-105"
                        : "bg-white text-gray-700 border-brandAccentColor hover:bg-brandAccentColor/50"
                        }`}
                    >
                      <div className="text-base font-bold">
                        {new Date(date).getDate()}
                      </div>
                      <div className="text-xs opacity-80">
                        {new Date(date).toLocaleDateString("en-US", {
                          weekday: "short",
                        })}
                      </div>
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => navigateDateWindow(1)}
                className="p-3 rounded-xl hover:bg-gray-100 transition-colors"
              >
                <FiChevronRight className="text-xl text-gray-600" />
              </button>
            </div>

            {/* Today shortcut */}
            <div className="flex justify-center">
              <button
                onClick={goToToday}
                disabled={selectedDate === todayISO}
                className="px-4 py-1 flex items-center text-[0.65rem] font-medium text-accentTextColor hover:bg-accentTextColor/30 rounded-lg transition-colors"
              >
                {selectedDate !== todayISO ? "Back to Today" : "Today"}
              </button>
            </div>
          </div>
        </div>

        {/* Slots Grid */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
          {loading ? (
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {Array.from({ length: 8 }).map((_, i) => (
                  <SlotCardSkeleton key={i} />
                ))}
              </div>
            </div>
          ) : slots.length === 0 ? (
            isBeyondAutoWindow(selectedDate) ? (
              <FutureNoSlotsView
                onGenerate={() => setShowGenerateModal(true)}
              />
            ) : (
              <div className="flex flex-col items-center justify-center py-20 text-gray-400">
                <FiClock className="text-6xl mb-4 opacity-30" />
                <p className="text-lg font-medium">
                  No slots available for this date
                </p>
                <p className="text-sm mt-2">
                  Slots will appear automatically if configured
                </p>
              </div>
            )
          ) : (
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {slots.map((slot) => (
                  <div
                    key={slot.availabilityId}
                    className={`relative rounded-lg border-2 p-3 transition-all duration-200 ${slot.isBlocked
                      ? "border-red-200 bg-red-50"
                      : slot.slotStatus === "inactive"
                        ? "border-gray-200 bg-gray-50 opacity-60"
                        : "border-green-200 bg-gradient-to-br from-green-50 to-emerald-50 hover:shadow-lg "
                      }`}
                  >
                    {/* Status Badge and Time Display */}
                    <div className="flex justify-between">
                      <div className="flex items-center gap-3">
                        <div className="text-gray-800 text-xs font-bold p-2 rounded-lg border border-accentTextColor">
                          <div className="  ">
                            {convertTo12Hour(slot.startTime)}
                          </div>
                          <div className="text-[0.65rem] w-full text-center text-secondaryTextColor">
                            to
                          </div>
                          <div className="">
                            {convertTo12Hour(slot.endTime)}
                          </div>
                        </div>
                      </div>
                      <div>
                        <SlotStatusBadge
                          isBlocked={slot.isBlocked}
                          slotStatus={slot.slotStatus}
                        />
                      </div>
                    </div>

                    {/* Bookings Count */}
                    <div className="flex items-center justify-between my-2">
                      <span className="text-xs font-semibold text-secondaryTextColor">
                        Bookings:
                      </span>
                      <span
                        className={`text-base font-bold ${slot.bookedCount > 0
                          ? "text-primaryTextColor"
                          : "text-gray-400"
                          }`}
                      >
                        {slot.bookedCount}
                      </span>
                    </div>

                    <div className="h-10 flex items-center gap-2">
                      {slot.isBlocked ? (
                        <>
                          {/* Mini Unblock Button */}
                          <button
                            onClick={() => toggleBlockStatus(slot)}
                            className="w-full px-4 py-2.5 rounded-lg font-semibold text-sm bg-gradient-to-r from-green-500 to-emerald-500 text-white gap-2 hover:shadow-md transition-all flex items-center justify-center"
                            title="Unblock Slot"
                          >
                            <FiUnlock className="text-sm" />
                            Unblock Slot
                          </button>
                        </>
                      ) : (
                        /* Full-width Block Button */
                        <button
                          onClick={() => toggleBlockStatus(slot)}
                          disabled={slot.slotStatus === "inactive"}
                          className={`w-full px-4 py-2.5 rounded-lg font-semibold text-sm transition-all duration-200 flex items-center justify-center gap-2 ${slot.slotStatus === "inactive"
                            ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                            : "bg-gradient-to-r from-accentTextColor/60 to-accentTextColor text-white hover:shadow-md"
                            }`}
                        >
                          <FiLock />
                          Block Slot
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Block Modal */}
      {showBlockModal && (
        <BlockSlotModal
          slot={showBlockModal}
          onClose={() => setShowBlockModal(null)}
          onBlock={handleBlockSlot}
        />
      )}

      {/* Generate Modal */}
      {showGenerateModal && (
        <GenerateSlotsModal
          branchId={selectedBranchId}
          onClose={() => setShowGenerateModal(false)}
          onSuccess={() => {
            setShowGenerateModal(false);
            fetchAvailability(selectedDate);
          }}
        />
      )}
    </div>
  );
};

const BlockSlotModal = ({ slot, onClose, onBlock }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {


    setIsSubmitting(true);
    await onBlock(slot);
    setIsSubmitting(false);
  };

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 animate-in fade-in duration-200"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md animate-in zoom-in-95 duration-200"
      >
        <div className="sticky top-0 bg-gradient-to-r from-accentTextColor to-accentTextColor/70 text-white px-6 py-5 rounded-t-2xl">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold">Block Slot</h2>
            <X
              onClick={onClose}
              className="size-6 cursor-pointer hover:scale-110 transition-transform"
            />
          </div>
          <p className="text-red-100 text-sm mt-1">
            Prevent bookings for this slot
          </p>
        </div>

        <div className="p-6">
          {/* Slot Info */}
          <div className="mb-6 p-4 bg-gray-50 rounded-xl border border-gray-200">
            <div className="flex items-center gap-3 mb-2">
              <FiClock className="text-[#f0653a] text-xl" />
              <div className="text-base font-bold text-gray-800">
                {convertTo12Hour(slot.startTime)} -{" "}
                {convertTo12Hour(slot.endTime)}
              </div>
            </div>
            {slot.slotName && (
              <div className="text-sm text-gray-600">{slot.slotName}</div>
            )}
          </div>
          <p className="text-sm text-gray-500">
            This will block the slot and prevent users from booking it.
          </p>
        </div>
        <div className="flex gap-3 px-6 pb-6">
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="flex-1 px-6 py-3 rounded-xl border border-gray-300 text-gray-700 font-semibold hover:bg-gray-50 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="flex-1 px-6 py-3 rounded-xl bg-gradient-to-r from-accentTextColor to-accentTextColor/70 text-white font-semibold active:scale-[0.98] transition-all duration-200 disabled:opacity-80 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isSubmitting ? (
              <>
                <ImSpinner2 className="w-5 h-5 animate-spin" />
                Blocking...
              </>
            ) : (
              <>
                <FiLock />
                Block Slot
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const GenerateSlotsModal = ({ onClose, onSuccess, branchId }) => {
  const [startDate, setStartDate] = useState(getTodayISO());
  const [endDate, setEndDate] = useState(addDays(getTodayISO(), 30));
  const [isSubmitting, setIsSubmitting] = useState(false);

  // console.log("slectedbranch:", branchId);

  const handleSubmit = async () => {
    if (!startDate || !endDate) {
      showError("Please select both start and end dates");
      return;
    }

    if (new Date(startDate) > new Date(endDate)) {
      showError("Start date must be before end date");
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/slot-availability/generate`,
        {
          startDate,
          endDate,
          branchId,
        },
      );
      showSuccess("Slot availability generated successfully");
      onSuccess();
    } catch (err) {
      console.error("Failed to generate slots", err);
      showError(
        err?.response?.data?.message || "Failed to generate slot availability",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md animate-in zoom-in-95 duration-200">
        <div className="sticky top-0 bg-gradient-to-r  from-accentTextColor to-accentTextColor/70 text-white px-6 py-5 rounded-t-2xl">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold">Generate Slot Availability</h2>
            <X
              onClick={onClose}
              className="size-6 cursor-pointer hover:scale-110 transition-transform"
            />
          </div>
          <p className="text-orange-100 text-sm mt-1">
            Create availability for date range
          </p>
        </div>

        <div className="p-6 space-y-5">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Start Date
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full rounded-lg px-4 py-3 outline-none ring-1 ring-gray-300 focus:ring-[#f0653a] transition-all duration-200"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              End Date
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full rounded-lg px-4 py-3 outline-none ring-1 ring-gray-300 focus:ring-[#f0653a] transition-all duration-200"
            />
          </div>

          <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
            <p className="text-sm text-blue-700">
              <strong>Note:</strong> This will create availability records for
              all active slot templates within the selected date range.
            </p>
          </div>
        </div>

        <div className="flex gap-3 px-6 pb-6">
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="flex-1 px-6 py-3 rounded-xl border border-gray-300 text-gray-700 font-semibold hover:bg-gray-50 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="flex-1 px-6 py-3 rounded-xl bg-gradient-to-r  from-accentTextColor to-accentTextColor/90 text-white font-semibold active:scale-[0.98] transition-all duration-200 disabled:opacity-80 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isSubmitting ? (
              <>
                <ImSpinner2 className="w-5 h-5 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <FiRefreshCw />
                Generate
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const SlotCardSkeleton = () => {
  return (
    <div className="rounded-lg border-2 p-3 animate-pulse bg-gray-50 border-gray-200">
      {/* Header: time + status */}
      <div className="flex justify-between">
        <div className="space-y-1">
          <div className="h-4 w-16 bg-gray-300 rounded" />
          <div className="h-3 w-10 bg-gray-200 rounded mx-auto" />
          <div className="h-4 w-16 bg-gray-300 rounded" />
        </div>

        <div className="h-5 w-20 bg-gray-300 rounded-full" />
      </div>

      {/* Booking count */}
      <div className="flex justify-between items-center my-3">
        <div className="h-3 w-16 bg-gray-200 rounded" />
        <div className="h-4 w-6 bg-gray-300 rounded" />
      </div>

      {/* Action area */}
      <div className="h-10 w-full bg-gray-300 rounded-lg" />
    </div>
  );
};

const FutureNoSlotsView = ({ onGenerate }) => {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center">
      <div className="w-16 h-16 rounded-full bg-orange-100 flex items-center justify-center mb-4">
        <FiClock className="text-3xl text-accentTextColor" />
      </div>

      <h3 className="text-lg font-semibold text-gray-800">
        No slots generated for this date
      </h3>

      <p className="text-sm text-gray-500 mt-2 max-w-md">
        Slots are automatically generated only for the next 7 days. To manage
        availability for this future date, you can generate slots manually.
      </p>

      <button
        onClick={onGenerate}
        className="mt-6 px-5 py-2.5 rounded-lg bg-gradient-to-r from-accentTextColor to-accentTextColor/80 text-white text-sm font-semibold hover:shadow-md transition-all flex items-center gap-2"
      >
        <FiRefreshCw />
        Generate Now
      </button>
    </div>
  );
};

export default AdminSlotsAvailability;
